/**
 * InfiniteScrollCollection
 *
 * Progressively enhances the collection product grid with infinite scroll.
 * Native pagination is preserved in the DOM for crawlers and no-JS users;
 * it is only suppressed visually when JavaScript is active.
 *
 * Compatible with:
 *  - Shopify Dawn section rendering (facets.js filter/sort AJAX)
 *  - Faceted filtering (all filter types: horizontal, vertical, drawer)
 *  - Browser back/forward via FacetFiltersForm popstate handler
 *  - Quick add (standard + bulk), variant selectors, scroll animations
 */
(function () {
  'use strict';

  class InfiniteScrollCollection {
    // IntersectionObserver margin: load next page when sentinel is within 500px of viewport bottom
    static #ROOT_MARGIN = '0px 0px 500px 0px';

    #container;
    #sentinel = null;
    #intersectionObserver = null;
    #mutationObserver = null;
    #reinitPending = false;
    #fetching = false;
    #abortController = null;

    constructor(container) {
      this.#container = container;
      this.#init();
      this.#watchContainerMutations();
    }

    // ─── Getters (always reflect current DOM state after innerHTML swaps) ───

    get #grid() {
      return this.#container.querySelector('#product-grid');
    }

    get #paginationWrapper() {
      return this.#container.querySelector('.pagination-wrapper');
    }

    /** Next-page URL as written by Shopify's paginate object (relative URL). */
    get #nextPageUrl() {
      return this.#paginationWrapper?.dataset.nextPage ?? null;
    }

    /** Full qualified section ID (e.g. "template--22224696705326__main"). */
    get #sectionId() {
      return this.#grid?.dataset.id ?? null;
    }

    // ─── Core lifecycle ───────────────────────────────────────────────────────

    #init() {
      // Cancel any in-flight fetch from a previous cycle (e.g. mid-load filter change)
      this.#abortController?.abort();
      this.#abortController = null;
      this.#fetching = false;

      this.#disconnectIntersectionObserver();
      this.#removeSentinel();

      if (!this.#grid || !this.#nextPageUrl) return;

      // Suppress native pagination visually. It stays in the DOM so crawlers and
      // no-JS users still find the page links in the raw HTML source.
      const pagination = this.#paginationWrapper;
      if (pagination) {
        pagination.style.display = 'none';
        pagination.setAttribute('aria-hidden', 'true');
      }

      this.#attachSentinel();
      this.#setupIntersectionObserver();
    }

    // ─── Sentinel element ────────────────────────────────────────────────────

#attachSentinel() {
  const sentinel = document.createElement('div');

  sentinel.setAttribute('aria-hidden', 'true');

  sentinel.style.cssText = `
    width: 100%;
    height: 1px;
    display: block;
  `;

  this.#sentinel = sentinel;

  // insert before pagination
  this.#grid.parentNode.insertBefore(
    sentinel,
    this.#paginationWrapper
  );

  console.log(
    'sentinel rect',
    sentinel.getBoundingClientRect()
  );
}

    #removeSentinel() {
      // Safe to call on already-detached nodes (e.g. after innerHTML swap)
      this.#sentinel?.remove();
      this.#sentinel = null;
    }

    // ─── IntersectionObserver ────────────────────────────────────────────────

    #setupIntersectionObserver() {
      console.log("hitt 3")
      if (!this.#sentinel){
        const loader = document.querySelector(".collection-pagination-loader .loading__spinner");
        if(loader){
              loader?.classList.add('hidden');
            }
return;

      } 
 console.log("hitt 5")
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
           console.log("hitt 6");
           console.log(entries[0].isIntersecting)
          if (entries[0].isIntersecting && !this.#fetching) {
             
            this.#loadNextPage();
            
          }
          else{
            const loader = document.querySelector(".collection-pagination-loader .loading__spinner");
            if(loader){
              loader?.classList.add('hidden');
            }
            
          }
        },
        { rootMargin: InfiniteScrollCollection.#ROOT_MARGIN }
      );

      this.#intersectionObserver.observe(this.#sentinel);
    }

    #disconnectIntersectionObserver() {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
    }

    // ─── Fetch + render ──────────────────────────────────────────────────────

    #loadNextPage() {
      const loader = document.querySelector(".collection-pagination-loader .loading__spinner")
      const nextUrl = this.#nextPageUrl;
      const sectionId = this.#sectionId;
      if (!nextUrl || !sectionId){
        loader?.classList.add('hidden');
        return;
      } 
      loader?.classList.remove('hidden');
      this.#fetching = true;

      // Build the Shopify section-rendering URL: preserve all existing params
      // (filters, sort_by, page) and append section_id for a partial response.
      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      this.#abortController = new AbortController();

      fetch(fetchUrl.toString(), { signal: this.#abortController.signal })
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.#appendItems(doc);
          this.#advance(doc);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('[InfiniteScroll]', err);
          }
        })
        .finally(() => {
          this.#fetching = false;
          this.#abortController = null;
          loader?.classList.add('hidden');
        });
    }

    #appendItems(doc) {
      const grid = this.#grid;
      if (!grid) return;

      const newItems = doc.querySelectorAll('#product-grid > .grid__item');
      if (!newItems.length) return;

      // Build a set of existing product URL pathnames to prevent duplicates
      // card__media-link is the anchor wrapping the product image in card-product.liquid
      const existingUrls = new Set(
        Array.from(grid.querySelectorAll('a.card__media-link')).map(
          (a) => new URL(a.getAttribute('href'), window.location.origin).pathname
        )
      );

      const fragment = document.createDocumentFragment();

      newItems.forEach((item) => {
        // Dedup: skip if this product is already in the grid
        const link = item.querySelector('a.card__media-link');
        if (link) {
          const pathname = new URL(link.getAttribute('href'), window.location.origin).pathname;
          if (existingUrls.has(pathname)) return;
          existingUrls.add(pathname); // guard against dupes within the same batch
        }

        // Remove the class Dawn uses to cancel scroll animations after AJAX filter renders
        item.classList.remove('scroll-trigger--cancel');

        fragment.appendChild(item);
      });

      if (!fragment.childNodes.length) return;

      grid.appendChild(fragment);

      // Reinitialize Dawn scroll-in animations for the newly appended items.
      // initializeScrollAnimationTrigger is defined globally in animations.js.
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(grid);
      }
    }

    /** Update pagination state after a successful fetch, or clean up when exhausted. */
    #advance(doc) {
      const newPagination = doc.querySelector('.pagination-wrapper');
      const nextUrl = newPagination?.dataset.nextPage;

      if (!nextUrl) {
        // No further pages: tear down observer infrastructure.
        // The hidden native pagination remains in DOM (no-JS / SEO fallback).
        this.#disconnectIntersectionObserver();
        this.#removeSentinel();
        return;
      }

      // Persist the new next-page URL so the following scroll trigger fetches the right page
      const pagination = this.#paginationWrapper;
      if (pagination) {
        pagination.dataset.nextPage = nextUrl;
        if (newPagination.dataset.page) {
          pagination.dataset.page = newPagination.dataset.page;
        }
      }
    }

    // ─── MutationObserver: react to filter/sort grid replacement ────────────

    /**
     * facets.js replaces #ProductGridContainer.innerHTML on every filter/sort change.
     * That wipes our sentinel. We watch direct children of the container so that
     * our own DOM mutations (appending items to #product-grid inside) don't re-trigger.
     */
    #watchContainerMutations() {
      this.#mutationObserver = new MutationObserver(() => {
        // Debounce: a single innerHTML swap can fire multiple MutationObserver callbacks
        if (this.#reinitPending) return;
        this.#reinitPending = true;
        requestAnimationFrame(() => {
          this.#reinitPending = false;
          this.#init();
        });
      });

      // childList: true + subtree: false → only direct children of #ProductGridContainer.
      // Appending items to the nested #product-grid ul will NOT trigger this observer.
      this.#mutationObserver.observe(this.#container, { childList: true });
    }
  }

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('ProductGridContainer');
    if (container) new InfiniteScrollCollection(container);
  });
})();
